import { OBPermissions } from '@alicetms/auth.permissions';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { booleanAttribute, EventEmitter, Inject, Injectable, Output } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ClientService } from './client.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: HttpClient, @Inject(DOCUMENT) private document: Document, private clientService: ClientService, private router: Router) { }

  private authenticated = false;
  public logoutUrl: string = null;

  @Output() userProfile: UserProfile;
  private permissions: IBffUserInfoClaim[];
  private clientpermissions: IBffUserInfoClaim[];
  private settings: IBffUserInfoClaim[];
  private features: IBffUserInfoClaim[];
  private Givenname: string;

  login(returnUrl: string = '/') {
    this.document.location.href = `/bff/login?returnUrl=${encodeURI(returnUrl)}`;
  }

  logout() {
    this.document.location.href = this.logoutUrl;
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.authenticated) {
      await lastValueFrom(this.getUserInfo())
        .then((claims: IBffUserInfoClaim[] | any) => {
          this.authenticated = true;

          this.logoutUrl = this.getLogoutUrl(claims);

          const features = claims.filter((x) => x.type.toLowerCase().includes('ft:'));
          let userId = claims.find(x => x.type == "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier")?.value ?? ""; 
          this.features = features;

          this.userProfile = {
            email: this.getEmail(claims),
            name: this.getFullName(claims),
            pictureUrl: '/assets/layout/images/profile-image.png',
            UserId:  userId
          };

          const profilePictureClaim = claims.find((claim: IBffUserInfoClaim) => claim.type === 'avatar');

          this.getClientClaims(claims);

          if (profilePictureClaim) {
            this.userProfile.pictureUrl = profilePictureClaim.value;
          }

          this.permissions = claims.filter((claim: IBffUserInfoClaim) => claim.type === 'permission');

          this.clientpermissions = claims.filter((claim: IBffUserInfoClaim) => claim.type === 'client:permission');

          this.settings = claims.filter((claim: IBffUserInfoClaim) => claim.type === 'client:setting');
        })
        .catch((err) => {
          console.error('Failed to get user info', err);
          this.logoutUrl = this.getLogoutUrl(err.error);
          this.authenticated = false;
          if (err.status === 401) {
            this.login();
          }
          if (err.status === 403) {
            this.authenticated = true;
            this.router.navigate(['forbidden']);
          }
        });
    }

    return this.authenticated;
  }

  private getClientClaims(claims: any) {
    this.clientService.clientTenantName = this.filterClaims(claims, '__tenant__')[0];
    this.clientService.clientTenant = this.filterClaims(claims, 'tenant:name')[0];
    this.clientService.clientPermissions = this.filterClaims(claims, 'client:permission');
    this.clientService.clientAgreements = this.filterClaims(claims, 'client:agreement');
    this.clientService.SetServicesFromAuthService(this.filterClaims(claims, 'client:service'));
    this.clientService.clientAccounts = this.filterClaims(claims, 'client:account');
    this.clientService.clientPayers = this.filterClaims(claims, 'client:payer');
    this.clientService.defaultClientPhone = this.filterClaims(claims, 'client:phone')[0];
    this.clientService.defaultClientEmail = this.filterClaims(claims, 'client:email')[0];
    this.clientService.defaultClientName = this.filterClaims(claims, 'client:name')[0];
    this.clientService.defaultClientNumber = this.filterClaims(claims, 'client:number')[0];
    this.clientService.defaultClientContactPerson = this.filterClaims(claims, 'client:contactPerson')[0];
    this.clientService.clientAddress = this.filterClaims(claims, 'client:address')[0];
    this.clientService.clientName = this.filterClaims(claims, 'client:name')[0];
    this.clientService.countries = this.filterClaims(claims, 'client:countries')[0];
    this.clientService.clientId = this.filterClaims(claims, '__clientId__')[0];
    this.clientService.clientTenantLogo = this.filterClaims(claims, 'tenant:logoUri')[0];
    this.clientService.clientReadyDeadline = this.filterClaims(claims, 'client:readyDeadline')[0];
    this.clientService.WebAutoReady = this.filterClaims(claims, 'client:WebAutoReady')[0];
    this.clientService.ClientAllowedToBookOnHoliDay = claims.map(x => x.value).filter(x => x == "ob.book.BookOnHoliday").length > 0;
    this.clientService.HeaderTextItem = this.filterClaims(claims, "client:StandartHeaderItemText")[0] ?? "";
    this.Givenname = this.filterClaims(claims, "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname")[0] ?? "";
  }

  private filterClaims(claims: IBffUserInfoClaim[], type: string): any[] {
    return claims.filter((claim) => claim.type === type).map((claim) => claim.value);
  }

  private getLogoutUrl(claims: IBffUserInfoClaim[]): string {
    // if(claims == undefined || claims == null)
    //   return "https://localhost/logout";
    const logoutUrlClaim = claims.find((claim: IBffUserInfoClaim) => claim.type === 'bff:logout_url');
    if (logoutUrlClaim) {
      return logoutUrlClaim.value;
    }
    return null;
  }

  private getFullName(claims: IBffUserInfoClaim[]): string {
    const prefix = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims';
    let fullName = 'User';
    const givenName = claims.find((claim) => claim.type === `${prefix}/givenname`);
    const surName = claims.find((claim) => claim.type === `${prefix}/surname`);
    if (givenName) {
      fullName = givenName.value;
    }
    if (surName) {
      fullName = `${fullName} ${surName.value}`;
    }
    return fullName.trim();
  }

  private getEmail(claims: IBffUserInfoClaim[]): string {
    var claimEmail = claims.find((claim: IBffUserInfoClaim) => claim.type === 'email');

    if (claimEmail) {
      return claimEmail.value;
    }

    return null;
  }

  private getUserInfo() {
    return this.http.get('/bff/user', {
      headers: new HttpHeaders({
        'X-CSRF': '1',
      }),
    });
  }

  hasPermission(permission: OBPermissions | OBPermisssionsLocal): boolean {

    var res: boolean = this.permissions.some((claim: IBffUserInfoClaim) => claim.value == permission);
    if (!res) {
      res = this.clientpermissions.some((claim: IBffUserInfoClaim) => claim.value == permission);
    }
    return res;

    //return this.permissions.some((claim: IBffUserInfoClaim) => claim.value == permission);
  }

  hasSetting(setting: string): boolean {    
    return this.settings.some((claim: IBffUserInfoClaim) => claim.value == setting);
  }

  hasFeature(feature: string): boolean {
    return this.features.some((claim: IBffUserInfoClaim) => claim.type.toLowerCase().includes(feature.toLowerCase()) && claim.value);
  }

  /**
   * 
   * @returns weather or not this client can jump to markAsReadyBooking
   */
  HasWebAutoReady(): boolean {
    return this.clientService.WebAutoReady;
  }

  isAliceAdmin(): boolean {
    return this.userProfile.email == "support@alicetms.dk" && this.Givenname == "Alice";
  }
  ChangePassword(password: string) {    
    return this.http.post(`${environment.apiUrl}/users/${this.userProfile.UserId}/setPassword`, { userId:this.userProfile.UserId,newPassword: password.trim() });
  }

}

export interface IBffUserInfoClaim {
  type: string;
  value: string;
}

export interface UserProfile {
  name: string;
  email: string;
  pictureUrl: string;
  UserId?:string | any;
}

export enum OBPermisssionsLocal {
  BookDeliveries = "ob.book.deliveries",
  BookPickups = "ob.book.pickups",
  BookWithPickupDate = "ob.book.withpickupdate",
  BookWithCrane = "ob.book.crane",
  BookDangerousGoods = "ob.book.adr",
  BookFranco = "ob.book.franco",
  BookEXW = "ob.book.exw",
  BookPostNord = "ob.book.postnord",
  BookPostNordBusiness = "ob.book.postnord.business",
  BookPostNordMyPack = "ob.book.postnord.mypack",
  BookPostNordCollect = "ob.book.postnord.collect",
  BookGLS = "ob.book.gls",
  BookGLSAbroad = "ob.book.gls.abroad",
  BookGLSPrivate = "ob.book.gls.private",
  UploadEDIFiles = "ob.edi.upload",
  PrintLabels = "ob.labels.print",
  PrintWaybill = "ob.waybill.print",
  ShowPackagingLists = "ob.packaginglists.show",
  ShowPrices = "ob.prices.show",
  ShowTodaysShipments = "ob.shipments.today.show",
  ShowNotReady = "ob.shipments.notready.show",
  MarkShipmentAsReady = "ob.shipments.notready.markready",
  ShowArchive = "ob.shipments.archive.show",
  ShowCustomers = "ob.customers.show",
  CreateCustomer = "ob.customers.create",
  ShowMyPage = "ob.mypage.show",
  ShowMyPageInvoices = "ob.mypage.invoices.show",
  ShowMyPageKPI = "ob.mypage.kpi.show",
  BookOnHoliday = "ob.book.BookOnHoliday",
  PalletSaldo = "ob.PalletSaldo",
  printLabelBeforeReady = "ob.label.printLabelBeforeReady"
}
